import React, { useState, useEffect, useCallback } from 'react';
import { usersApi, UserData } from '../services/api';

interface UserManagerProps {
  onUsersChange?: () => void;
}

export function UserManager({ onUsersChange }: UserManagerProps): React.ReactElement {
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserData | null>(null);
  const [formUsername, setFormUsername] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formDisplayName, setFormDisplayName] = useState('');
  const [formRole, setFormRole] = useState<'admin' | 'viewer'>('viewer');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete confirmation
  const [userToDelete, setUserToDelete] = useState<UserData | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const loadUsers = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (err: any) {
      setError(err?.message || 'Erro ao carregar usuários');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  const resetForm = () => {
    setFormUsername('');
    setFormPassword('');
    setFormDisplayName('');
    setFormRole('viewer');
    setEditingUser(null);
    setShowForm(false);
  };

  const handleEdit = (user: UserData) => {
    setEditingUser(user);
    setFormUsername(user.username);
    setFormPassword('');
    setFormDisplayName(user.displayName);
    setFormRole(user.role);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingUser) {
        const updateData: any = {};
        if (formUsername !== editingUser.username) updateData.username = formUsername;
        if (formPassword) updateData.password = formPassword;
        if (formDisplayName !== editingUser.displayName) updateData.displayName = formDisplayName;
        if (formRole !== editingUser.role) updateData.role = formRole;

        await usersApi.update(editingUser.id, updateData);
        setSuccessMessage(`Usuário "${formUsername}" atualizado!`);
      } else {
        await usersApi.create({
          username: formUsername,
          password: formPassword,
          displayName: formDisplayName || formUsername,
          role: formRole
        });
        setSuccessMessage(`Usuário "${formUsername}" criado!`);
      }
      resetForm();
      loadUsers();
      if (onUsersChange) onUsersChange();
    } catch (err: any) {
      setError(err?.message || 'Erro ao salvar usuário');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleToggleActive = async (user: UserData) => {
    try {
      await usersApi.update(user.id, { isActive: !user.isActive });
      setSuccessMessage(`Usuário "${user.username}" ${user.isActive ? 'desativado' : 'ativado'}!`);
      loadUsers();
    } catch (err: any) {
      setError(err?.message || 'Erro ao alterar status');
    }
  };

  const handleConfirmDelete = async () => {
    if (!userToDelete) return;
    setIsDeleting(true);
    try {
      await usersApi.delete(userToDelete.id);
      setSuccessMessage(`Usuário "${userToDelete.username}" removido!`);
      setUserToDelete(null);
      loadUsers();
      if (onUsersChange) onUsersChange();
    } catch (err: any) {
      setError(err?.message || 'Erro ao remover usuário');
      setUserToDelete(null);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div style={{ padding: '1.5rem', background: '#fff', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '0.75rem', borderBottom: '2px solid #eee' }}>
        <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#2c3e50' }}>👥 Gerenciar Usuários</h2>
        <button
          type="button"
          onClick={() => { resetForm(); setShowForm(true); }}
          style={{ padding: '0.5rem 1rem', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}
        >
          + Novo Usuário
        </button>
      </div>

      {error && (
        <div style={{ padding: '0.75rem', background: '#f8d7da', color: '#721c24', borderRadius: '6px', marginBottom: '1rem', display: 'flex', justifyContent: 'space-between' }}>
          {error}
          <button onClick={() => setError(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
        </div>
      )}

      {successMessage && (
        <div style={{ padding: '0.75rem', background: '#d4edda', color: '#155724', borderRadius: '6px', marginBottom: '1rem' }}>
          {successMessage}
        </div>
      )}

      {/* Formulário */}
      {showForm && (
        <div style={{ padding: '1.5rem', background: '#f8f9fa', borderRadius: '8px', marginBottom: '1.5rem', border: '1px solid #e9ecef' }}>
          <h3 style={{ margin: '0 0 1rem 0', color: '#333' }}>{editingUser ? '✏️ Editar Usuário' : '➕ Novo Usuário'}</h3>
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>Usuário *</label>
                <input
                  type="text"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  required
                  disabled={isSubmitting}
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                  placeholder="Nome de usuário"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>
                  Senha {editingUser ? '(deixe vazio para manter)' : '*'}
                </label>
                <input
                  type="password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  required={!editingUser}
                  disabled={isSubmitting}
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                  placeholder={editingUser ? '••••••' : 'Senha'}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>Nome de Exibição</label>
                <input
                  type="text"
                  value={formDisplayName}
                  onChange={(e) => setFormDisplayName(e.target.value)}
                  disabled={isSubmitting}
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                  placeholder="Nome completo"
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 600, fontSize: '0.9rem', color: '#333' }}>Papel</label>
                <select
                  value={formRole}
                  onChange={(e) => setFormRole(e.target.value as 'admin' | 'viewer')}
                  disabled={isSubmitting}
                  style={{ width: '100%', padding: '0.625rem', border: '1px solid #ddd', borderRadius: '6px', fontSize: '0.95rem', boxSizing: 'border-box' }}
                >
                  <option value="viewer">Visualizador</option>
                  <option value="admin">Administrador</option>
                </select>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
              <button type="button" onClick={resetForm} disabled={isSubmitting} style={{ padding: '0.5rem 1rem', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button type="submit" disabled={isSubmitting} style={{ padding: '0.5rem 1rem', background: '#2c3e50', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                {isSubmitting ? 'Salvando...' : editingUser ? 'Salvar' : 'Criar Usuário'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tabela de usuários */}
      {isLoading ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>Carregando...</div>
      ) : users.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#6c757d' }}>Nenhum usuário cadastrado.</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.95rem' }}>
            <thead>
              <tr style={{ background: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Usuário</th>
                <th style={{ padding: '0.75rem', textAlign: 'left' }}>Nome</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Papel</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Status</th>
                <th style={{ padding: '0.75rem', textAlign: 'center' }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id} style={{ borderBottom: '1px solid #e9ecef' }}>
                  <td style={{ padding: '0.75rem', fontWeight: 600 }}>{user.username}</td>
                  <td style={{ padding: '0.75rem', color: '#6c757d' }}>{user.displayName}</td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      background: user.role === 'admin' ? '#fff3cd' : '#e3f2fd',
                      color: user.role === 'admin' ? '#856404' : '#1565c0'
                    }}>
                      {user.role === 'admin' ? '🔑 Admin' : '👁️ Visualizador'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <span style={{
                      padding: '0.25rem 0.5rem',
                      borderRadius: '12px',
                      fontSize: '0.8rem',
                      fontWeight: 500,
                      background: user.isActive ? '#d4edda' : '#f8d7da',
                      color: user.isActive ? '#155724' : '#721c24'
                    }}>
                      {user.isActive ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleEdit(user)}
                        title="Editar"
                        style={{ width: '32px', height: '32px', border: 'none', borderRadius: '6px', background: '#e3f2fd', cursor: 'pointer', fontSize: '1rem' }}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleToggleActive(user)}
                        title={user.isActive ? 'Desativar' : 'Ativar'}
                        style={{ width: '32px', height: '32px', border: 'none', borderRadius: '6px', background: user.isActive ? '#fff3cd' : '#d4edda', cursor: 'pointer', fontSize: '1rem' }}
                      >
                        {user.isActive ? '🚫' : '✅'}
                      </button>
                      <button
                        onClick={() => setUserToDelete(user)}
                        title="Excluir"
                        style={{ width: '32px', height: '32px', border: 'none', borderRadius: '6px', background: '#ffebee', cursor: 'pointer', fontSize: '1rem' }}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de confirmação de exclusão */}
      {userToDelete && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: '#fff', borderRadius: '8px', padding: '1.5rem', maxWidth: '400px', width: '90%' }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Confirmar Exclusão</h3>
            <p>Tem certeza que deseja excluir o usuário <strong>"{userToDelete.username}"</strong>?</p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.5rem' }}>
              <button onClick={() => setUserToDelete(null)} disabled={isDeleting} style={{ padding: '0.5rem 1rem', background: '#6c757d', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                Cancelar
              </button>
              <button onClick={handleConfirmDelete} disabled={isDeleting} style={{ padding: '0.5rem 1rem', background: '#dc3545', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 600 }}>
                {isDeleting ? 'Excluindo...' : 'Excluir'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserManager;
